/**
 * Submit Data Page — Wraps QuestionnaireRenderer
 *
 * Flow:
 *   1. Select fund (if investee is in multiple funds)
 *   2. Select template (from templates linked to that fund)
 *   3. Enter reporting period (year + quarter)
 *   4. Renders QuestionnaireRenderer with pre-populated data
 *   5. On submit: POST /esg/submissions/ then POST /{id}/submit
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { QuestionnaireRenderer } from '@/components/questionnaire/QuestionnaireRenderer';

// ─── Types ──────────────────────────────────────────────────────

interface FundOption {
  id: number;
  name: string;
}

interface TemplateOption {
  id: number;
  name: string;
  template_type: string;
  version?: string;
}

type PageState = 'setup' | 'filling' | 'submitted';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const QUARTER_OPTIONS = [
  { value: 'Q1', label: 'Q1 (Jan - Mar)' },
  { value: 'Q2', label: 'Q2 (Apr - Jun)' },
  { value: 'Q3', label: 'Q3 (Jul - Sep)' },
  { value: 'Q4', label: 'Q4 (Oct - Dec)' },
  { value: 'Annual', label: 'Annual' },
];

// ─── Main Component ─────────────────────────────────────────────

export function SubmitDataPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const investeeId = user?.id ?? 0;

  const [pageState, setPageState] = useState<PageState>('setup');
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch funds available to this investee
  const fundsQuery = useQuery<FundOption[]>({
    queryKey: ['ic', 'investee', 'funds', investeeId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/investees/${investeeId}/funds`);
      const data = res.data?.data ?? res.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60_000,
    enabled: !!investeeId,
  });

  // Fetch templates linked to selected fund
  const templatesQuery = useQuery<TemplateOption[]>({
    queryKey: ['ic', 'esg', 'templates', 'fund', selectedFundId],
    queryFn: async () => {
      const url = selectedFundId
        ? `/api/v1/impact-core/esg/templates/?fund_id=${selectedFundId}`
        : '/api/v1/impact-core/esg/templates/';
      const res = await apiClient.get(url);
      const data = res.data?.data ?? res.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60_000,
    enabled: selectedFundId !== null,
  });

  // Create submission mutation
  const createSubmissionMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      // Step 1: Create submission
      const createRes = await apiClient.post('/api/v1/impact-core/esg/submissions/', {
        investee_id: investeeId,
        fund_id: selectedFundId,
        template_id: selectedTemplateId,
        reporting_period: `${selectedQuarter} ${selectedYear}`,
        data,
      });
      const submission = createRes.data?.data ?? createRes.data;
      const submissionId = submission.id as number;

      // Step 2: Submit the submission
      await apiClient.post(`/api/v1/impact-core/esg/submissions/${submissionId}/submit`);

      return submissionId;
    },
    onSuccess: (id) => {
      setSubmittedId(id);
      setPageState('submitted');
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to submit. Please try again.';
      setSubmitError(message);
    },
  });

  // Save draft mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post('/api/v1/impact-core/esg/submissions/', {
        investee_id: investeeId,
        fund_id: selectedFundId,
        template_id: selectedTemplateId,
        reporting_period: `${selectedQuarter} ${selectedYear}`,
        data,
        status: 'draft',
      });
      return res.data?.data ?? res.data;
    },
  });

  const handleSubmit = useCallback(
    (data: Record<string, unknown>) => {
      setSubmitError(null);
      createSubmissionMutation.mutate(data);
    },
    [createSubmissionMutation],
  );

  const handleSaveDraft = useCallback(
    (data: Record<string, unknown>) => {
      saveDraftMutation.mutate(data);
    },
    [saveDraftMutation],
  );

  const canProceed = selectedFundId !== null && selectedTemplateId !== null;

  const handleStartQuestionnaire = useCallback(() => {
    if (canProceed) {
      setPageState('filling');
    }
  }, [canProceed]);

  // ─── Submitted State ─────────────────────────────────────────

  if (pageState === 'submitted') {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
            <CheckCircle className="h-6 w-6 text-emerald-400" />
          </div>
          <h2 className="text-lg font-semibold text-ic-text-primary">Submission Complete</h2>
          <p className="mt-1 text-xs text-ic-text-tertiary">
            Your ESG data has been submitted successfully.
            {submittedId && <span className="ml-1 font-mono text-ic-text-secondary">#{submittedId}</span>}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/submissions')}
              className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
            >
              View Submissions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Questionnaire Filling State ──────────────────────────────

  if (pageState === 'filling' && selectedTemplateId && selectedFundId) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-ic-text-tertiary">
          <button
            onClick={() => setPageState('setup')}
            className="hover:text-ic-text-secondary transition-colors"
          >
            Submit Data
          </button>
          <ChevronRight className="h-3 w-3" />
          <span className="text-ic-text-primary">{selectedQuarter} {selectedYear}</span>
        </div>

        {/* Submission Error */}
        {submitError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <span className="text-xs text-red-400">{submitError}</span>
            </div>
          </div>
        )}

        {/* Save Draft Status */}
        {saveDraftMutation.isSuccess && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-400">Draft saved successfully</span>
            </div>
          </div>
        )}

        {createSubmissionMutation.isPending && (
          <div className="rounded-lg border border-ic-border bg-ic-card p-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-4 w-4 animate-spin text-ic-accent" />
              <span className="text-xs text-ic-text-secondary">Submitting your data...</span>
            </div>
          </div>
        )}

        <QuestionnaireRenderer
          templateId={selectedTemplateId}
          fundId={selectedFundId}
          investeeId={investeeId}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
        />
      </div>
    );
  }

  // ─── Setup State ──────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">Submit ESG Data</h1>
        <p className="text-xs text-ic-text-tertiary">
          Select a fund, template, and reporting period to begin
        </p>
      </div>

      <div className="max-w-xl space-y-5">
        {/* Step 1: Select Fund */}
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ic-accent/15 text-[10px] font-semibold text-ic-accent">
              1
            </span>
            <label className="text-xs font-medium text-ic-text-primary">Select Fund</label>
          </div>

          {fundsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-ic-text-tertiary">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading funds...
            </div>
          ) : fundsQuery.isError ? (
            <p className="text-xs text-red-400">Failed to load funds.</p>
          ) : (fundsQuery.data ?? []).length === 0 ? (
            <p className="text-xs text-ic-text-tertiary">
              No funds assigned. Contact your fund manager.
            </p>
          ) : (
            <select
              value={selectedFundId ?? ''}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                setSelectedFundId(val);
                setSelectedTemplateId(null);
              }}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:outline-none focus:ring-1 focus:ring-ic-accent/50"
            >
              <option value="">Select a fund...</option>
              {(fundsQuery.data ?? []).map((fund) => (
                <option key={fund.id} value={fund.id}>
                  {fund.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Step 2: Select Template */}
        <div className={clsx(
          'rounded-lg border bg-ic-card p-4 transition-opacity',
          selectedFundId ? 'border-ic-border opacity-100' : 'border-ic-border/50 opacity-50',
        )}>
          <div className="flex items-center gap-2 mb-3">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ic-accent/15 text-[10px] font-semibold text-ic-accent">
              2
            </span>
            <label className="text-xs font-medium text-ic-text-primary">Select Template</label>
          </div>

          {!selectedFundId ? (
            <p className="text-xs text-ic-text-tertiary">Select a fund first</p>
          ) : templatesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-ic-text-tertiary">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading templates...
            </div>
          ) : templatesQuery.isError ? (
            <p className="text-xs text-red-400">Failed to load templates.</p>
          ) : (templatesQuery.data ?? []).length === 0 ? (
            <p className="text-xs text-ic-text-tertiary">
              No templates available for this fund.
            </p>
          ) : (
            <select
              value={selectedTemplateId ?? ''}
              onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:outline-none focus:ring-1 focus:ring-ic-accent/50"
            >
              <option value="">Select a template...</option>
              {(templatesQuery.data ?? []).map((tmpl) => (
                <option key={tmpl.id} value={tmpl.id}>
                  {tmpl.name} ({tmpl.template_type}{tmpl.version ? ` v${tmpl.version}` : ''})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Step 3: Reporting Period */}
        <div className={clsx(
          'rounded-lg border bg-ic-card p-4 transition-opacity',
          selectedTemplateId ? 'border-ic-border opacity-100' : 'border-ic-border/50 opacity-50',
        )}>
          <div className="flex items-center gap-2 mb-3">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ic-accent/15 text-[10px] font-semibold text-ic-accent">
              3
            </span>
            <label className="text-xs font-medium text-ic-text-primary">Reporting Period</label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[10px] text-ic-text-tertiary">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                disabled={!selectedTemplateId}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:outline-none focus:ring-1 focus:ring-ic-accent/50 disabled:opacity-50"
              >
                {YEAR_OPTIONS.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] text-ic-text-tertiary">Quarter</label>
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                disabled={!selectedTemplateId}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:outline-none focus:ring-1 focus:ring-ic-accent/50 disabled:opacity-50"
              >
                {QUARTER_OPTIONS.map((q) => (
                  <option key={q.value} value={q.value}>{q.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Start Button */}
        <button
          onClick={handleStartQuestionnaire}
          disabled={!canProceed}
          className={clsx(
            'flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold transition-colors',
            canProceed
              ? 'bg-ic-accent text-white hover:bg-ic-accent-hover'
              : 'bg-slate-700/50 text-slate-500 cursor-not-allowed',
          )}
        >
          <ClipboardCheck className="h-4 w-4" />
          Begin Questionnaire
        </button>
      </div>
    </div>
  );
}
